/**
 * Low-level scroll helpers for the campaigns carousel.
 *
 * Extracted to keep useCampaignsCarousel focused on React state
 * and to document the Safari-specific workarounds in one place.
 */

/**
 * Stop Safari's momentum (inertial) scroll.
 *
 * Safari's compositor keeps scrolling even after a programmatic
 * `scrollLeft` assignment.  Briefly toggling `overflowX` to `hidden`
 * forces the compositor to start a new scroll session, effectively
 * killing the momentum. The current scroll position is preserved.
 */
export function stopMomentumScroll(container: HTMLElement): void {
  const pos = container.scrollLeft;
  container.style.overflowX = 'hidden';
  void container.offsetHeight;           // commit to compositor
  container.style.overflowX = '';
  container.scrollLeft = pos;
}

/**
 * Disable scroll-snap so a programmatic scroll isn't fought by the
 * browser's snap engine (Safari re-snaps immediately using
 * mid-transition card dimensions).
 */
export function disableScrollSnap(container: HTMLElement): void {
  container.style.scrollSnapType = 'none';
  container.style.scrollBehavior = 'auto';
}

/**
 * Re-enable scroll-snap after it was disabled.
 */
export function restoreScrollSnap(container: HTMLElement): void {
  container.style.scrollSnapType = '';
  container.style.scrollBehavior = '';
}

/**
 * Calculate the scrollLeft value that centres a child element inside
 * its scrollable parent.
 */
export function getCenteredScrollLeft(
  container: HTMLElement,
  element: HTMLElement,
): number {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  return Math.max(
    0,
    elementRect.left - containerRect.left + container.scrollLeft
      - containerRect.width / 2
      + elementRect.width / 2,
  );
}
